/**
 * SamvadSessionLogger — Log a solo or paired Sanskrit conversation session
 * Sprint 2: solo-first design; partner ID is optional field (BN-2 mitigation).
 * YouTube URL is optional per BN-7 (minor students may not have channels).
 */
import { useState, useEffect } from 'react';
import '../styles/SamvadSessionLogger.css';

interface Props {
  onNavigate: (path: string) => void;
}

// Book list mirrors SamvadLanding.tsx BOOKS — single source of truth can be
// extracted to a shared constant in a later sprint when a shared lib is added.
const BOOKS = [
  { id: 'nrivarg',           titleHi: 'नृवर्ग — अमरकोश',        subtitleEn: 'Rakkad Village Dialogue' },
  { id: 'kshatriyavarg',     titleHi: 'क्षत्रियवर्ग — अमरकोश',  subtitleEn: 'Gau Vaṃśa Dialogue' },
  { id: 'vaishyavarg-khand1', titleHi: 'गोवर्धन — खण्ड १',      subtitleEn: 'Govardhana Vol. 1 · Bīja' },
  { id: 'vaishyavarg-khand2', titleHi: 'गोवर्धन — खण्ड २',      subtitleEn: 'Govardhana Vol. 2 · Aṅkura' },
  { id: 'vaishyavarg-khand3', titleHi: 'गोवर्धन — खण्ड ३',      subtitleEn: 'Govardhana Vol. 3 · Phala' },
  { id: 'nishedha-madira',   titleHi: 'निषेध-मदिरा',            subtitleEn: 'Niṣedha-Madirā Dialogue' },
  { id: 'vyaghra-valmiki',   titleHi: 'व्याघ्र की महिमाएँ',     subtitleEn: 'Glory of Tigers — Amarakosha' },
  { id: 'vaanara-samvad',    titleHi: 'वानर-संवाद',              subtitleEn: 'Vānara Saṃvāda — Amarakosha' },
  { id: 'varaaha-samvad',    titleHi: 'वाराह-संवाद',             subtitleEn: 'Varāha Saṃvāda — Amarakosha' },
  { id: 'saagar-manthan',    titleHi: 'सागर-मंथन',               subtitleEn: 'Sāgara-Manthana' },
  { id: 'geedh',             titleHi: 'गीधकी-आँख',              subtitleEn: 'Eye of the Kite' },
  { id: 'singh-amara',       titleHi: 'सिंह की महिमाएँ',        subtitleEn: 'Glory of Lions — Amarakosha' },
  { id: 'hanuman-bahuk',     titleHi: 'हनुमान बाहुक',            subtitleEn: 'Hanumān Bāhuk — Tulsidas' },
  { id: 'vanyagraamya-pashu', titleHi: 'वन्यग्राम्य पशु',       subtitleEn: 'Wild and Village Animals' },
];

const today = () => new Date().toISOString().slice(0, 10);

interface ProgressData {
  participant_id: string;
  total_hours: number;
  sessions_count: number;
  books_covered: string[];
  milestone_badges: string[];
  next_milestone_hours: number | null;
  next_milestone_label: string | null;
}

interface SessionResult {
  session_id: string;
  hours_this_session: number;
  progress: ProgressData;
  new_milestones: { id: string; label: string; label_en: string }[];
}

interface ActiveMatch {
  match_id: string;
  partner_id: string;
  partner_display: string;
}

export default function SamvadSessionLogger({ onNavigate }: Props) {
  const [participantId, setParticipantId] = useState(
    () => localStorage.getItem('samvad_participant_id') ?? ''
  );
  const [sessionType, setSessionType] = useState<'solo' | 'paired'>('solo');
  const [partnerId, setPartnerId] = useState('');
  const [partnerSelectValue, setPartnerSelectValue] = useState('');
  const [activeMatches, setActiveMatches] = useState<ActiveMatch[]>([]);
  const [loadingMatches, setLoadingMatches] = useState(false);
  const [bookId, setBookId] = useState('');
  const [date, setDate] = useState(today());
  const [durationMinutes, setDurationMinutes] = useState<number | ''>(45);
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [reflection, setReflection] = useState('');

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<SessionResult | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  // Load active matches when switching to paired mode
  useEffect(() => {
    if (sessionType !== 'paired' || !participantId.trim()) {
      setActiveMatches([]);
      return;
    }
    setLoadingMatches(true);
    fetch(`/api/samvad/match/active/${participantId.trim()}`)
      .then(r => r.json())
      .then(d => { setActiveMatches(d.active_matches ?? []); setLoadingMatches(false); })
      .catch(() => setLoadingMatches(false));
  }, [sessionType, participantId]);

  function validate(): boolean {
    const errs: Record<string, string> = {};
    if (!participantId.trim()) errs.participantId = 'प्रतिभागी ID आवश्यक है (पंजीकरण के समय दी गई)';
    if (!bookId) errs.bookId = 'कृपया एक पुस्तक चुनें';
    if (!date) errs.date = 'दिनांक आवश्यक है';
    const dur = Number(durationMinutes);
    if (!durationMinutes || isNaN(dur) || dur < 10 || dur > 300) {
      errs.duration = 'अवधि 10–300 मिनट होनी चाहिए';
    }
    if (sessionType === 'paired') {
      // Use matched partner if selected, else fall back to manual entry
      const effectivePartner = partnerSelectValue === '__manual__' || !partnerSelectValue
        ? partnerId.trim()
        : partnerSelectValue;
      if (!effectivePartner) errs.partnerId = 'युगल प्रतिभागी ID आवश्यक है';
    }
    if (reflection.trim().length < 20) errs.reflection = 'कम से कम 20 अक्षर लिखें';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function clearError(field: string) {
    setErrors(e => { const n = { ...e }; delete n[field]; return n; });
  }

  async function handleSubmit() {
    if (!validate()) return;
    setSubmitting(true);
    setServerError(null);
    try {
      const payload: Record<string, unknown> = {
        participant_id:   participantId.trim(),
        session_type:     sessionType,
        book_id:          bookId,
        date:             date,
        duration_minutes: Number(durationMinutes),
        reflection:       reflection.trim(),
      };
      if (sessionType === 'paired') {
        const effectivePartner = partnerSelectValue === '__manual__' || !partnerSelectValue
          ? partnerId.trim()
          : partnerSelectValue;
        if (effectivePartner) payload.partner_id = effectivePartner;
      }
      if (youtubeUrl.trim()) {
        payload.youtube_url = youtubeUrl.trim();
      }
      const res = await fetch('/api/samvad/sessions/log', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { detail?: string }).detail ?? `Server error ${res.status}`);
      }
      const data = await res.json() as SessionResult;
      setResult(data);
    } catch (err) {
      setServerError(err instanceof Error ? err.message : 'नेटवर्क त्रुटि — पुनः प्रयास करें');
    } finally {
      setSubmitting(false);
    }
  }

  // ── Success screen ───────────────────────────────────────────────────────
  if (result) {
    const p = result.progress;
    const pct = p.next_milestone_hours
      ? Math.min(100, (p.total_hours / p.next_milestone_hours) * 100)
      : 100;

    return (
      <div className="svl-page">
        <div className="svl-success">
          <div className="svl-success-icon">📝</div>
          <h2>सत्र सफलतापूर्वक लॉग हुआ!</h2>
          <p className="svl-success-sub">Session ID: <code>{result.session_id}</code></p>

          {result.new_milestones.length > 0 && (
            <div className="svl-milestone-alert">
              <div className="svl-milestone-icon">🏅</div>
              <div>
                <strong>उपलब्धि प्राप्त!</strong>
                {result.new_milestones.map(m => (
                  <div key={m.id} className="svl-milestone-name">
                    {m.label} <span>({m.label_en})</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="svl-progress-card">
            <div className="svl-progress-row">
              <span className="svl-progress-label">कुल घण्टे</span>
              <span className="svl-progress-value">{p.total_hours.toFixed(1)} hrs</span>
            </div>
            <div className="svl-progress-row">
              <span className="svl-progress-label">कुल सत्र</span>
              <span className="svl-progress-value">{p.sessions_count}</span>
            </div>
            <div className="svl-progress-row">
              <span className="svl-progress-label">पुस्तकें</span>
              <span className="svl-progress-value">{p.books_covered.length}</span>
            </div>
            {p.next_milestone_hours && (
              <div className="svl-progress-bar-wrap">
                <div className="svl-progress-bar-labels">
                  <span>{p.total_hours.toFixed(1)} hrs</span>
                  <span>अगला लक्ष्य: {p.next_milestone_label} ({p.next_milestone_hours}h)</span>
                </div>
                <div className="svl-progress-bar-track">
                  <div className="svl-progress-bar-fill" style={{ width: `${pct}%` }} />
                </div>
              </div>
            )}
            {p.milestone_badges.length > 0 && (
              <div className="svl-badges">
                {p.milestone_badges.map(b => (
                  <span key={b} className="svl-badge">🏅 {b}</span>
                ))}
              </div>
            )}
          </div>

          <div className="svl-success-actions">
            <button className="svl-btn-primary" onClick={() => {
              setResult(null);
              setReflection('');
              setDate(today());
            }}>
              + एक और सत्र लॉग करें
            </button>
            <button className="svl-btn-secondary" onClick={() => onNavigate('/samvad')}>
              ← संवाद गृहपृष्ठ
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Log form ─────────────────────────────────────────────────────────────
  return (
    <div className="svl-page">
      <button className="svl-back-btn" onClick={() => onNavigate('/samvad')}>
        ← संवाद
      </button>

      <div className="svl-card">
        <div className="svl-card-header">
          <div className="svl-header-badge">संस्कृत युवा संवाद कार्यक्रम</div>
          <h1 className="svl-card-title">सत्र लॉग करें</h1>
          <p className="svl-card-sub">Log a Conversation Session</p>
        </div>

        <div className="svl-form">

          {/* Participant ID */}
          <div className="svl-field">
            <label className="svl-label">प्रतिभागी ID <span className="svl-req">*</span></label>
            <input
              className={`svl-input ${errors.participantId ? 'svl-input--error' : ''}`}
              placeholder="SV-2026-00001"
              value={participantId}
              onChange={e => { setParticipantId(e.target.value); clearError('participantId'); }}
            />
            {errors.participantId && <span className="svl-error">{errors.participantId}</span>}
            <span className="svl-hint">
              पंजीकरण के समय प्राप्त ID ·{' '}
              <button type="button" className="svl-link-btn" onClick={() => onNavigate('/samvad/onboarding')}>
                अभी पंजीकरण करें →
              </button>
            </span>
          </div>

          {/* Session type */}
          <div className="svl-field">
            <label className="svl-label">सत्र प्रकार <span className="svl-req">*</span></label>
            <div className="svl-type-row">
              <button
                type="button"
                className={`svl-type-btn ${sessionType === 'solo' ? 'svl-type-btn--selected' : ''}`}
                onClick={() => setSessionType('solo')}
              >
                🧘 एकल (Solo)
              </button>
              <button
                type="button"
                className={`svl-type-btn ${sessionType === 'paired' ? 'svl-type-btn--selected' : ''}`}
                onClick={() => setSessionType('paired')}
              >
                🤝 युगल (Paired)
              </button>
            </div>
          </div>

          {/* Partner — smart select when active matches exist, manual fallback */}
          {sessionType === 'paired' && (
            <div className="svl-field svl-field--inset">
              <label className="svl-label">युगल प्रतिभागी · Partner <span className="svl-req">*</span></label>
              {loadingMatches ? (
                <p style={{ fontSize: '0.85rem', color: 'var(--muted-text)', margin: 0 }}>सक्रिय साझेदार लोड हो रहे हैं…</p>
              ) : activeMatches.length > 0 ? (
                <>
                  <select
                    className={`svl-select ${errors.partnerId ? 'svl-input--error' : ''}`}
                    value={partnerSelectValue}
                    onChange={e => { setPartnerSelectValue(e.target.value); clearError('partnerId'); }}
                  >
                    <option value="">— साझेदार चुनें —</option>
                    {activeMatches.map(m => (
                      <option key={m.match_id} value={m.partner_id}>
                        {m.partner_display} ({m.partner_id})
                      </option>
                    ))}
                    <option value="__manual__">✏ अन्य (manual entry)</option>
                  </select>
                  {partnerSelectValue === '__manual__' && (
                    <input
                      className={`svl-input ${errors.partnerId ? 'svl-input--error' : ''}`}
                      placeholder="SV-2026-00002"
                      value={partnerId}
                      onChange={e => { setPartnerId(e.target.value); clearError('partnerId'); }}
                      style={{ marginTop: '0.4rem' }}
                    />
                  )}
                </>
              ) : (
                <input
                  className={`svl-input ${errors.partnerId ? 'svl-input--error' : ''}`}
                  placeholder="SV-2026-00002"
                  value={partnerId}
                  onChange={e => { setPartnerId(e.target.value); clearError('partnerId'); }}
                />
              )}
              {errors.partnerId && <span className="svl-error">{errors.partnerId}</span>}
              {activeMatches.length === 0 && !loadingMatches && (
                <p className="svl-hint">
                  अभी कोई सक्रिय साझेदार नहीं — <button type="button" className="svl-link-btn" onClick={() => onNavigate('/samvad/dashboard')}>डैशबोर्ड से खोजें</button>
                </p>
              )}
            </div>
          )}

          {/* Book */}
          <div className="svl-field">
            <label className="svl-label">पुस्तक <span className="svl-req">*</span></label>
            <select
              className={`svl-select ${errors.bookId ? 'svl-input--error' : ''}`}
              value={bookId}
              onChange={e => { setBookId(e.target.value); clearError('bookId'); }}
            >
              <option value="">— पुस्तक चुनें —</option>
              {BOOKS.map(b => (
                <option key={b.id} value={b.id}>
                  {b.titleHi} · {b.subtitleEn}
                </option>
              ))}
            </select>
            {errors.bookId && <span className="svl-error">{errors.bookId}</span>}
            {bookId && (
              <button
                type="button"
                className="svl-link-btn"
                onClick={() => onNavigate(`/products/${bookId}`)}
              >
                पुस्तक खोलें →
              </button>
            )}
          </div>

          {/* Date + Duration */}
          <div className="svl-row">
            <div className="svl-field">
              <label className="svl-label">दिनांक <span className="svl-req">*</span></label>
              <input
                type="date"
                className={`svl-input ${errors.date ? 'svl-input--error' : ''}`}
                value={date}
                max={today()}
                onChange={e => { setDate(e.target.value); clearError('date'); }}
              />
              {errors.date && <span className="svl-error">{errors.date}</span>}
            </div>
            <div className="svl-field">
              <label className="svl-label">अवधि (मिनट) <span className="svl-req">*</span></label>
              <input
                type="number"
                className={`svl-input ${errors.duration ? 'svl-input--error' : ''}`}
                min={10} max={300} step={5}
                placeholder="45"
                value={durationMinutes}
                onChange={e => {
                  setDurationMinutes(e.target.value === '' ? '' : Number(e.target.value));
                  clearError('duration');
                }}
              />
              {errors.duration
                ? <span className="svl-error">{errors.duration}</span>
                : <span className="svl-hint">
                    {durationMinutes ? `≈ ${(Number(durationMinutes) / 60).toFixed(2)} घण्टे` : '10–300 मिनट'}
                  </span>
              }
            </div>
          </div>

          {/* YouTube URL (optional) */}
          <div className="svl-field">
            <label className="svl-label">YouTube URL <span className="svl-optional">(वैकल्पिक)</span></label>
            <input
              className="svl-input"
              type="url"
              placeholder="https://youtube.com/..."
              value={youtubeUrl}
              onChange={e => setYoutubeUrl(e.target.value)}
            />
            <span className="svl-hint">सत्र की रिकॉर्डिंग हो तो URL दें — न हो तो खाली छोड़ें।</span>
          </div>

          {/* Reflection */}
          <div className="svl-field">
            <label className="svl-label">
              चिन्तन-टिप्पणी <span className="svl-req">*</span>
            </label>
            <textarea
              className={`svl-textarea ${errors.reflection ? 'svl-input--error' : ''}`}
              rows={4}
              placeholder="आज के सत्र में क्या सीखा? कौन-सा शब्द या वाक्य याद रहा? संस्कृत में या हिंदी में लिख सकते हैं।"
              value={reflection}
              onChange={e => { setReflection(e.target.value); clearError('reflection'); }}
            />
            <span className="svl-hint">{reflection.trim().length} / कम से कम 20 अक्षर</span>
            {errors.reflection && <span className="svl-error">{errors.reflection}</span>}
          </div>

          {serverError && (
            <div className="svl-server-error">⚠ {serverError}</div>
          )}

          <div className="svl-submit-row">
            <button
              className="svl-btn-primary"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? '…सबमिट हो रहा है' : '✓ सत्र लॉग करें'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
